import { useId, useMemo, useState } from 'react';
import { PartitionOutlined } from '@ant-design/icons';

import { DataTable, Tile } from '../../../../components/shared';
import { Button, Empty, Select, Skeleton } from '../../../../components/ui';
import { useT } from '../../../../i18n';
import type { KhoaDich } from '../../../../i18n';
import { KHOI_BANG, LE_BANG } from '../../chung/bang';
import { KhoiLoi } from '../../chung/components/KhoiLoi';
import { soNguyen } from '../../chung/nghiep-vu/so-thong-ke';
import { TRONG, dayDu, gio } from '../../chung/nghiep-vu/thoi-gian';
import type { DongLuong } from '../../chung/nghiep-vu/types-vet';
import { useLuong } from '../hooks';
import { cotFox, cotTokenThat, LOP_O_SO } from './cot-fox';
import { GiaVon, NhanGiaThieu } from './NhanTinCay';
import { nhanTenant, useNguonVet } from '../nguon';
import { ChuoiBuoc } from './ChuoiBuoc';

/**
 * Luồng theo đối tượng nghiệp vụ: một dòng mỗi vụ việc — một văn bản, một hồ sơ — gom hết các chuỗi xử lý đã chạm vào
 * nó. Port `vet/BangLuong.tsx` native.
 *
 * Không có ô sắp xếp, không có "Tải thêm": `/vet/luong` chỉ trả MỘT trang đã sắp theo token giảm dần. Màn này
 * nói thẳng ở chân bảng rằng nó là bảng xếp hạng.
 *
 * Danh sách loại đối tượng của ô lọc lấy từ chính dữ liệu đã tải: `/vet/luong` không có endpoint danh mục, và bịa ra
 * một danh sách cố định ở giao diện là một bản sao sẽ trôi khỏi dữ liệu thật.
 */

const TAT_CA = '';

function CacTrace({ cac, onMo }: { cac: string[] | null; onMo: (traceId: string) => void }) {
  const { t } = useT();
  if (!cac || cac.length === 0) {
    return <p className="text-xs text-content-muted">{t('nktvSuDungAi.luong.khongCoTrace')}</p>;
  }
  return (
    <div className="space-y-1 py-1">
      <p className="text-xs text-content-muted">{t('nktvSuDungAi.luong.cacTrace')}</p>
      <ul className="flex flex-wrap gap-2">
        {cac.map((traceId) => (
          <li key={traceId}>
            <Button variant="outline" size="sm" onClick={() => onMo(traceId)}>
              <span className="font-mono text-xs">{traceId}</span>
            </Button>
          </li>
        ))}
      </ul>
    </div>
  );
}

export function BangLuong({ tu, den }: { tu: string; den: string }) {
  const { t, locale } = useT();
  const idLoai = useId();
  const [loai, setLoai] = useState<string>(TAT_CA);
  const { cotTenant, tenTenant, nenTang } = useNguonVet();
  const [dangXem, setDangXem] = useState<{ traceId: string; tenantId?: string } | null>(null);

  const thamSo = useMemo(() => (loai === TAT_CA ? { tu, den } : { tu, den, doi_tuong_loai: loai }), [tu, den, loai]);
  const truyVan = useLuong(thamSo);
  const dong = useMemo(() => truyVan.data?.du_lieu ?? [], [truyVan.data]);

  const chonLoai = useMemo(() => {
    const co = new Set<string>(loai === TAT_CA ? [] : [loai]);
    for (const d of dong) if (d.doi_tuong_loai) co.add(d.doi_tuong_loai);
    return [
      { value: TAT_CA, label: t('nktvSuDungAi.luong.loaiTatCa') },
      ...[...co].sort((a, b) => a.localeCompare(b, locale)).map((m) => ({ value: m, label: m })),
    ];
    // `t` là hàm mới mỗi lần vẽ; nhãn chỉ đổi khi đổi ngôn ngữ (`locale`).
  }, [dong, loai, locale]);

  return (
    <div className="space-y-4">
      <Tile className={KHOI_BANG}>
        <div className="flex flex-wrap items-center gap-2 border-b border-default px-5 py-3">
          <label className="text-xs text-content-muted" htmlFor={idLoai}>
            {t('nktvSuDungAi.luong.loai')}
          </label>
          <Select<string> id={idLoai} value={loai} onChange={setLoai} options={chonLoai} className="min-w-[11rem]" />
        </div>
        {truyVan.isPending ? (
          <Skeleton className="m-4 h-72" />
        ) : truyVan.isError ? (
          <KhoiLoi loi={truyVan.error} onThuLai={() => void truyVan.refetch()} />
        ) : (
          <>
            <DataTable<DongLuong>
              caption={t('nktvSuDungAi.luong.bang')}
              className={LE_BANG}
              rowKey={(r) => `${r.tenant_id ?? ''}|${r.doi_tuong_loai ?? ''}|${r.doi_tuong_id ?? ''}`}
              size="small"
              dataSource={dong}
              pagination={false}
              expandable={{
                expandedRowRender: (r) => (
                  <CacTrace cac={r.cac_trace} onMo={(traceId) => setDangXem({ traceId, tenantId: r.tenant_id })} />
                ),
                expandRowByClick: true,
              }}
              locale={{
                emptyText:
                  loai !== TAT_CA ? (
                    <Empty icon={PartitionOutlined} title={t('nktvSuDungAi.danhSach.rongLoc')} />
                  ) : (
                    <Empty
                      icon={PartitionOutlined}
                      title={t('nktvSuDungAi.luong.rongTieuDe')}
                      description={t('nktvSuDungAi.luong.rongMoTa')}
                    />
                  ),
              }}
              columns={[
                {
                  title: t('nktvSuDungAi.luong.cotDoiTuong'),
                  width: 240,
                  render: (_v, r) => (
                    <div className="min-w-0 max-w-[14rem]">
                      <div className="truncate text-sm text-content-primary">
                        {r.doi_tuong_ten ?? r.doi_tuong_id ?? TRONG}
                      </div>
                      <div className="truncate text-xs text-content-muted">{r.doi_tuong_loai ?? TRONG}</div>
                    </div>
                  ),
                },
                ...(cotTenant
                  ? [
                      {
                        title: t('nktvSuDungAi.cotTenant'),
                        width: 160,
                        render: (_v: unknown, r: DongLuong) => (
                          <span className="block max-w-[10rem] truncate text-sm text-content-secondary">
                            {nhanTenant(tenTenant, r.tenant_id)}
                          </span>
                        ),
                      },
                    ]
                  : []),
                cotFox<DongLuong>(t, locale),
                ...(nenTang ? [cotTokenThat<DongLuong>(t, locale)] : []),
                ...(nenTang && dong.some((r) => r.chi_phi_von !== undefined)
                  ? [
                      {
                        title: t('nktvSuDungAi.cotGiaVon'),
                        width: 190,
                        align: 'right' as const,
                        render: (_v: unknown, r: DongLuong) => (
                          <span className="flex flex-wrap items-center justify-end gap-1">
                            <GiaVon giaTri={r.chi_phi_von} donVi={r.don_vi_tien ?? null} />
                            <NhanGiaThieu
                              coChuaCoGia={r.co_chua_co_gia}
                              coThieuTyGia={r.co_thieu_ty_gia}
                              coThieuDongGia={r.co_thieu_dong_gia}
                            />
                          </span>
                        ),
                      },
                    ]
                  : []),
                ...[
                  { khoa: 'nktvSuDungAi.luong.cotSoChuoi', doc: (r: DongLuong) => r.so_trace },
                  { khoa: 'nktvSuDungAi.trace.cotBuoc', doc: (r: DongLuong) => r.so_buoc },
                  { khoa: 'nktvSuDungAi.trace.cotLuotAi', doc: (r: DongLuong) => r.so_luot_llm },
                  { khoa: 'nktvSuDungAi.trace.cotLoi', doc: (r: DongLuong) => r.so_loi },
                  { khoa: 'nktvSuDungAi.luong.cotNguoi', doc: (r: DongLuong) => r.so_nguoi },
                ].map(({ khoa, doc }) => ({
                  title: t(khoa as KhoaDich),
                  key: khoa,
                  width: 72,
                  align: 'right' as const,
                  render: (_v: unknown, r: DongLuong) => (
                    <span
                      className={`${LOP_O_SO} ${khoa.endsWith('cotLoi') && r.so_loi > 0 ? 'font-medium text-danger-700' : 'text-content-secondary'}`}
                    >
                      {soNguyen(doc(r), locale)}
                    </span>
                  ),
                })),
                {
                  title: t('nktvSuDungAi.luong.cotKhoang'),
                  width: 150,
                  render: (_v, r) => (
                    <span
                      className="whitespace-nowrap font-mono text-xs text-content-secondary"
                      title={`${dayDu(r.bat_dau, locale)} → ${dayDu(r.ket_thuc, locale)}`}
                    >
                      {gio(r.bat_dau, locale)} → {gio(r.ket_thuc, locale)}
                    </span>
                  ),
                },
                {
                  title: t('nktvSuDungAi.luong.cotService'),
                  width: 180,
                  ellipsis: true,
                  render: (_v, r) => (
                    <span className="text-xs text-content-secondary" title={r.cac_service?.join(', ')}>
                      {r.cac_service?.filter(Boolean).join(', ') || TRONG}
                    </span>
                  ),
                },
              ]}
            />

            {dong.length > 0 && (
              <p className="p-4 text-xs text-content-muted" role="status">
                {t('nktvSuDungAi.luong.motTrang', { so: soNguyen(dong.length, locale) })}
              </p>
            )}
          </>
        )}
      </Tile>

      {dangXem !== null && (
        <ChuoiBuoc traceId={dangXem.traceId} tenantId={dangXem.tenantId} onDong={() => setDangXem(null)} />
      )}
    </div>
  );
}
